import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Zap, Crown, Loader2, Check, Sparkles, ArrowRight, Eye, Mic, Timer, FileCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import SampleAssessmentModal from "@/components/SampleAssessmentModal";

/**
 * UpgradeLiveModal - 30-second value pitch shown when a Free user clicks any
 * "Start Official AI Interview" CTA. Wraps the two upsell SKUs
 * (interview_ready_pass, career_pro) and hits the existing /billing/checkout
 * endpoint (beta auto-activate today, real gateway later).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onActivated?: (planId) => void - fired after successful activation.
 */
export default function UpgradeLiveModal({ open, onClose, onActivated }) {
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [showSample, setShowSample] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMsg("");
    setErr("");
  }, [open]);

  if (!open) return null;

  const doCheckout = async (id) => {
    setBusy(id);
    setMsg("");
    setErr("");
    try {
      const { data } = await api.post(`/billing/checkout/${id}`, {});
      if (data.kind === "beta_activated") {
        setMsg("Unlocked. Your Official AI Interview is ready.");
        onActivated && onActivated(id);
      } else {
        setMsg(data.message || "Request received.");
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="upgrade-live-modal"
      className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-md flex items-center justify-center p-4 animate-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl border border-amber-500/30 bg-gradient-to-br from-zinc-950 via-zinc-950 to-amber-950/25 p-7 shadow-[0_0_60px_-10px_rgba(245,158,11,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="upgrade-live-close"
          className="absolute top-3.5 right-3.5 text-zinc-500 hover:text-zinc-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400">
          <Sparkles className="h-3 w-3" /> Official AI Interview · Pro
        </div>
        <h2 className="mt-2 text-2xl font-bold tracking-tight text-zinc-50 leading-tight">
          Get your Official Interview Readiness Score.
        </h2>
        <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
          Free tier prepares you. Pro proves you&apos;re ready, with an executive-grade Official AI
          Interview and the 7-part Hiring Assessment recruiters actually respect.
        </p>

        <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs text-zinc-200">
          {[
            "Official Interview Readiness Score",
            "7-part Hiring Assessment report",
            "Voice-based AI interviewer",
            "Executive coaching verdict",
            "Downloadable Assessment PDF",
            "Unlimited Official AI Interview retakes",
          ].map((f) => (
            <li key={f} className="flex items-start gap-2">
              <Check className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={3} />
              {f}
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => setShowSample(true)}
          data-testid="upgrade-live-see-sample"
          className="mt-4 inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 underline underline-offset-4 decoration-amber-500/40 hover:decoration-amber-400 transition-colors"
        >
          <Eye className="h-3 w-3" />
          See a sample Assessment
        </button>

        {/* Time-to-value micro-timeline */}
        <div
          data-testid="upgrade-live-timeline"
          className="mt-5 rounded-xl border border-zinc-800/70 bg-zinc-900/50 px-4 py-3"
        >
          <div className="text-[9px] uppercase tracking-[0.24em] text-zinc-500 mb-2">
            Under 12 minutes, start to finish
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-full border border-amber-500/30 bg-amber-500/[0.08] flex items-center justify-center flex-shrink-0">
                <Mic className="h-3 w-3 text-amber-300" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 leading-none">10 min</div>
                <div className="text-[11px] text-zinc-200 font-medium mt-0.5 truncate">Official AI Interview</div>
              </div>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-amber-500/40 via-amber-500/20 to-transparent mx-1" />
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-full border border-amber-500/30 bg-amber-500/[0.08] flex items-center justify-center flex-shrink-0">
                <Timer className="h-3 w-3 text-amber-300" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 leading-none">~30 sec</div>
                <div className="text-[11px] text-zinc-200 font-medium mt-0.5 truncate">AI Analysis</div>
              </div>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-amber-500/40 via-amber-500/20 to-transparent mx-1" />
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-full border border-amber-500/40 bg-amber-500/[0.12] flex items-center justify-center flex-shrink-0">
                <FileCheck className="h-3 w-3 text-amber-300" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-amber-400/90 leading-none">Instant</div>
                <div className="text-[11px] text-zinc-100 font-semibold mt-0.5 truncate">Assessment + PDF</div>
              </div>
            </div>
          </div>
        </div>

        {msg && (
          <div
            data-testid="upgrade-live-success"
            className="mt-5 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-3 py-2"
          >
            {msg}
          </div>
        )}
        {err && (
          <div
            data-testid="upgrade-live-error"
            className="mt-5 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2"
          >
            {err}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            onClick={() => doCheckout("interview_ready_pass")}
            disabled={!!busy}
            data-testid="upgrade-live-pass-btn"
            className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-5 py-2.5 shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)] disabled:opacity-60"
          >
            {busy === "interview_ready_pass" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Zap className="h-3.5 w-3.5" />
            )}
            Try Interview Ready — free during beta
          </button>
          <button
            onClick={() => doCheckout("career_pro")}
            disabled={!!busy}
            data-testid="upgrade-live-pro-btn"
            className="inline-flex items-center gap-2 text-sm border border-zinc-700 hover:border-amber-500/60 hover:text-amber-100 text-zinc-100 rounded-lg px-4 py-2.5 disabled:opacity-60 transition-colors"
          >
            {busy === "career_pro" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Crown className="h-3.5 w-3.5" />
            )}
            Career Pro
          </button>
          <Link
            to="/pricing"
            data-testid="upgrade-live-compare"
            className="text-xs text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1 ml-auto"
          >
            Compare plans <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="mt-4 text-[10px] text-zinc-600 tracking-wide uppercase">
          Beta access. Payment gateway launching soon.
        </div>
      </div>

      <SampleAssessmentModal
        open={showSample}
        onClose={() => setShowSample(false)}
        onUnlockClick={() => {
          setShowSample(false);
          doCheckout("interview_ready_pass");
        }}
      />
    </div>
  );
}
